import { useState } from "react";
import { logger } from "@/lib/logger";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckList } from "@/components/CheckList";
import { UserRound } from "lucide-react";

// Admin: pick which operator accounts run this line. `operators` = users with role operator.
export function AssignOperatorsDialog({ machine, operators, machines, onClose, onSave }) {
  const [selected, setSelected] = useState(() => new Set(machine.operator_ids || []));
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const otherLines = (uid) => machines.filter((m) => m.id !== machine.id && (m.operator_ids || []).includes(uid)).map((m) => m.line_code);
  const items = [...operators]
    .sort((a, b) => (a.active === false) - (b.active === false) || a.name.localeCompare(b.name))
    .map((u) => {
      const others = otherLines(u.id);
      return { id: u.id, label: u.active === false ? `${u.name} (deactivated)` : u.name, sub: `@${u.username}`, muted: u.active === false,
        hint: others.length ? `also on ${others.join(", ")}` : "" };
    });

  const save = async () => {
    setSaving(true);
    try {
      await onSave(machine, [...selected]);
      onClose();
    } catch (err) {
      logger.error("Assign operators failed (toast shown by handler):", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="assign-operators-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><UserRound className="h-4 w-4 text-blue-600" /> Operators on {machine.line_code} · {machine.name}</DialogTitle>
          <DialogDescription className="text-xs">Assigned operators see only this line's jobs on their dashboard. An operator can run several lines.</DialogDescription>
        </DialogHeader>
        <CheckList items={items} selected={selected} onToggle={toggle} testidPrefix="assign-op" searchPlaceholder="Search operators…"
          emptyText="No operator accounts yet — create one on the Users tab first." />
        <DialogFooter>
          <span className="mr-auto text-xs text-slate-500 self-center" data-testid="assign-op-count">{selected.size} selected</span>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="assign-op-save">{saving ? "Saving…" : "Save Assignment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
